<script setup lang="ts">
import { useSlider } from '@/composables/helpers/slider'
import type { PlayerStats } from '@/types/shared/player'
import { usePlayer } from '@/composables/game/player'
import type { PlayerFortuneWheelState } from '@/types/shared/fortune-wheel'
import { MILESTONE_TYPES, type MilestoneReward } from '@/types/milestones'
import { isiOS } from '@/helpers/browser'
import { computed, watch } from 'vue'
import QuestMilestoneCard from '@/components/QuestMilestoneCard.vue'

const props = defineProps<{
	rewards: MilestoneReward[]
	type: MILESTONE_TYPES
	title: string
	description: string
	tagText: string
	icon: string
	claimLimit: number
	claimedRewards: number[]
}>()

defineEmits<{
	(e: 'onClaim', payload: {
		playerStats: PlayerStats,
		fortuneWheelState: PlayerFortuneWheelState
	}, reward: MilestoneReward): void
}>()

const startOffset = computed(() => {
  const firstClaimable = props.rewards.findIndex((reward) => props.claimLimit >= reward.minLimit && !props.claimedRewards?.includes(reward.minLimit))
  if (firstClaimable < 0) {
    const lastClaimed = [...props.rewards].reverse().findIndex((reward) => props.claimedRewards?.includes(reward.minLimit))
    if (lastClaimed < 0) {
      return 0
    }
    return Math.max(0, (props.rewards.length - lastClaimed - 1) * (90 + 12))
  }
  return Math.max(0, (firstClaimable - 1) * (90 + 12)) /* card width: 90px, gap: 12px */
})

const { onMouseDown, onMouseUp, onMouseMove, slider } = useSlider()
const { goldenCoinValue } = usePlayer()
const isIOSBrowser = isiOS()

watch([slider], () => {
  if (!slider.value) return
  slider.value.scrollLeft = startOffset.value
}, { immediate: true })

</script>
<template>
  <div class="overflow-hidden mt-3 rounded-xl bg-dark-bottom-layer py-3 w-full flex flex-col gap-y-3">
    <div class="px-3 w-full flex gap-x-3">
      <img
        :src="icon"
        class="size-12 min-w-[48px]"
      >
      <div class="flex-1 flex flex-col gap-y-0.5">
        <div class="text-lg font-semibold -tracking-[0.36px] flex flex-wrap items-center gap-x-1.5">
          <span>{{ title }}</span>
          <slot name="tooltip" />
        </div>
        <div class="flex gap-x-2 items-center">
          <div class="text-light-white text-sm font-semibold -tracking-[0.28px]">
            {{ description }}
          </div>
          <div
            class="rounded-full bg-dark-top-most-layer px-2.5 py-0.5 text-white text-sm font-semibold uppercase text-shadow-total-purchase"
          >
            {{ tagText }}
          </div>
        </div>
      </div>
    </div>
    <div
      ref="slider"
      class="flex overflow-x-auto items-center card-slider -m-6"
      @mousedown="onMouseDown"
      @mouseup="onMouseUp"
      @mouseleave="onMouseUp"
      @mousemove="onMouseMove"
    >
      <div class="flex-1 flex items-center gap-x-3 m-6">
        <QuestMilestoneCard
          v-for="(reward, index) in rewards"
          :key="index"
          :type="type"
          :reward="reward"
          class="h-[110px] w-[90px] min-w-[90px]"
          :class="{'ml-3': index === 0, [isIOSBrowser ? 'mr-9' : 'mr-3'] : index === rewards.length - 1}"
          :claimed="Boolean(claimedRewards?.includes(reward.minLimit))"
          :claimable="claimLimit >= reward.minLimit"
          :golden-coin-value="goldenCoinValue"
          @claim="$emit('onClaim', $event, reward)"
        />
      </div>
    </div>
  </div>
</template>